import {LitElement, css, html} from "lit";
import {styleMap} from "lit/directives/style-map.js";

export class AuiTooltip extends LitElement {

	static get properties() {
		return {
			text: { type: String, reflect: true },
			position: { type: String, reflect: true },
			delay: { type: Number, reflect: true }
		};
	}

	set for(id) {
		this.__id = id;
	}

	get for() {
		return this.previousElementSibling;
	}

	get Tooltip() {
		if(!this._tooltip)
			this._tooltip = this.shadowRoot.querySelector("#Tooltip");
		return this._tooltip;
	}

	constructor() {
		super();
		this._onOverFn = this.onOver.bind(this);
		this._onOutFn = this.onOut.bind(this);

		this.text = "";
		this.position = undefined;
		this.delay = 800;

		this.isOver = false;
		this.mousePos = undefined;
		this.delayFrame = null;
		this.isUpdatingPosition = false;
	}

	connectedCallback() {
		super.connectedCallback();

		if(this.for) {
			this.for.addEventListener("pointerenter", this._onOverFn);
			this.for.addEventListener("pointerleave", this._onOutFn);
		}
	}

	disconnectedCallback() {
		if(this.for) {
			this.for.removeEventListener("pointerleave", this._onOutFn);
			this.for.removeEventListener("pointerenter", this._onOverFn);
		}

		super.disconnectedCallback();
	}

	updated(changedProperties) {
		if(this.isUpdatingPosition) {
			this.isUpdatingPosition = false;
		} else {
			this.isUpdatingPosition = true;
			this.requestUpdate();
		}
	}

	getPosition() {
		const style = {};

		if(this.isOver) {
			if(this.isUpdatingPosition) {
				const container = this.previousElementSibling.getBoundingClientRect();
				const tooltip = this.Tooltip.getBoundingClientRect();

				const tooltipStyle = getComputedStyle(this.Tooltip);
				const tooltipMargin = {
					top: parseInt(tooltipStyle.marginTop),
					right: parseInt(tooltipStyle.marginRight),
					bottom: parseInt(tooltipStyle.marginBottom),
					left: parseInt(tooltipStyle.marginLeft),
				};

				tooltip.width += tooltipMargin.left + tooltipMargin.right;
				tooltip.height += tooltipMargin.top + tooltipMargin.bottom;

				switch(this.position) {
					case "top":
						style.top = container.top - (tooltip.height) + "px";
						style.left = container.left + (container.width / 2 - tooltip.width / 2) + "px";
						break;

					case "right":
						style.top = container.top + (container.height / 2 - tooltip.height / 2) + "px";
						style.left = container.right + "px";
						break;

					case "left":
						style.top = container.top + (container.height / 2 - tooltip.height / 2) + "px";
						style.left = container.left - (tooltip.width) + "px";
						break;

					case "mouse":
						style.top = this.mousePos.y + "px";
						style.left = this.mousePos.x + "px";
						break;

					case "bottom":
					default:
						style.top = container.bottom + "px";
						style.left = container.left + (container.width / 2 - tooltip.width / 2) + "px";
						break;
				}
			}
		} else {
			style.display = "none";
		}

		return style;
	}

	onOver(e) {
		//console.log("over", e.target);

		if(this.delay > 0) {
			this.delayFrame = setTimeout(() => {
				this.isOver = true;
				this.mousePos = { x: e.clientX, y: e.clientY };

				this.requestUpdate();
			}, this.delay);
		} else {
			this.requestUpdate();
		}
	}

	onOut(e) {
		//console.log("out", e.target);

		if(this.delayFrame !== null) {
			clearTimeout(this.delayFrame);
			this.delayFrame = null;
		}

		this.isOver = false;
		this.mousePos = undefined;

		this.requestUpdate();
	}

	static get styles() {
		return css`
			:host {
				display: inline-block;
			}
			
			.tooltip {
				background: black;
				
				position: fixed;
				margin: 5px;
				padding: 3px 5px;
				border-radius: 3px;
				
				font-size: 12px;
				font-weight: 400;
				letter-spacing: 0.011em;
				line-height: 20px;
				
				color: #FFF;
				
				pointer-events: none;
				z-index: 1000;
			}
		`;
	}

	render() {
		return html`<div id="Tooltip" class="tooltip" style=${styleMap(this.getPosition())}><slot></slot></div>`;
	}

}

export default AuiTooltip;

customElements.define("aui-tooltip", AuiTooltip);